import api from './api';
import type { SyncBadge } from './sales-invoice.service';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  meta?: { page?: number; limit?: number; total?: number };
}

export interface SalesSummaryByProductItem {
  row_key: string;
  product_id: string;
  product_code: string;
  product_name: string;
  uom: string;
  quantity: number;
  revenue: number;
  discount: number;
  return_qty: number;
  return_amount: number;
  reduction_amount: number;
  net_revenue: number;
  cost: number;
  gross_profit: number;
}

export interface SalesSummaryByProductTotals {
  quantity: number;
  revenue: number;
  discount: number;
  return_qty: number;
  return_amount: number;
  reduction_amount: number;
  net_revenue: number;
  cost: number;
  gross_profit: number;
}

export interface AccountingVoucherLineDescriptionUpdate {
  line_number: number;
  description: string;
}

export interface PostedAccountingVoucherUpdate {
  contact_name?: string;
  contact_address?: string;
  description?: string;
  lines: AccountingVoucherLineDescriptionUpdate[];
}

export interface AccountingAttachment {
  _id: string;
  id?: string;
  media_id?: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  description?: string;
}

export interface CreateAccountingAttachmentInput {
  media_id: string;
  owner_type: 'cash_voucher';
  owner_id: string;
  attachment_type: 'other';
  description?: string;
  replace_attachment_id?: string;
}

export interface AccountingRepairIssue {
  type:
    | 'journal_entry_unbalanced'
    | 'voucher_missing_journal_entry'
    | 'voucher_journal_entry_not_found'
    | 'voucher_journal_entry_not_posted'
    | 'voucher_journal_entry_source_mismatch'
    | 'voucher_journal_entry_amount_mismatch'
    | 'account_balance_mismatch'
    | 'account_balance_snapshot_mismatch'
    | 'fund_voucher_journal_mismatch'
    | 'sales_invoice_payment_status_mismatch'
    | 'purchase_invoice_payment_status_mismatch'
    | 'auto_fixable_voucher_journal_entry_link'
    | 'auto_fixable_missing_voucher_journal_entry'
    | 'auto_fixable_purchase_invoice_journal_entry_link'
    | 'auto_fixable_missing_purchase_invoice_journal_entry'
    | 'auto_fixable_fund_journal_account_mismatch'
    | 'journal_entry_posted_to_parent_account';
  severity: 'critical' | 'warning';
  document_id: string;
  document_number?: string;
  document_date?: string;
  message: string;
  expected?: number;
  actual?: number;
  delta?: number;
}

export interface AccountingRepairAuditResult {
  checked_at: string;
  summary: {
    issue_count: number;
    critical_count: number;
    warning_count: number;
    unbalanced_journal_entries: number;
    posted_vouchers_missing_journal_entry: number;
    posted_vouchers_with_missing_journal_entry: number;
    posted_pending_journal_entries: number;
    voucher_journal_entry_mismatches: number;
    account_balance_mismatches: number;
    account_balance_snapshot_mismatches: number;
    fund_balance_mismatches: number;
    invoice_payment_status_mismatches: number;
    journal_entries_posted_to_parent_account: number;
    auto_fixable_count: number;
  };
  issues: AccountingRepairIssue[];
}

export interface AccountingRepairAuditParams {
  page: number;
  page_size: number;
}

export interface AccountingBalanceRepairResult {
  repaired_at: string;
  dry_run: boolean;
  scanned_accounts: number;
  changed_accounts: number;
  changes: AccountingRepairIssue[];
}

export interface AccountingSafeRepairResult {
  repaired_at: string;
  dry_run: boolean;
  relinked_vouchers: number;
  recreated_missing_journal_entries: number;
  relinked_purchase_invoices: number;
  recreated_missing_purchase_invoice_journal_entries: number;
  reconciled_invoice_payment_statuses: number;
  reclassified_fund_journal_accounts: number;
  posted_pending_journal_entries: number;
  rebuilt_account_balances: number;
  changes: AccountingRepairIssue[];
}

interface DashboardSummaryParams {
  period?: string;
  from_date?: string;
  to_date?: string;
  store_id?: string;
}

interface RecentTransactionsParams {
  period?: string;
  from?: string;
  to?: string;
  page_size?: number;
  store_id?: string;
}

/**
 * Service for Accounting operations
 * Handles: Reports, Dashboard, Financial summaries
 */
/** Một dòng nhật ký thao tác của phiếu thu/chi (TCR-742). */
export interface VoucherHistoryEntry {
  event: 'CREATE' | 'UPDATE' | 'LOCK' | 'UNLOCK' | string;
  description: string;
  /** Lý do bỏ ghi sổ, nếu có. */
  reason: string | null;
  user_name: string | null;
  created_at: string | null;
  /** true khi mục được dựng lại từ created_at (phiếu có trước khi có nhật ký). */
  synthesized: boolean;
}

/** TCR-1436 — bộ lọc dùng chung giữa danh sách phiếu và số đếm trên badge. */
export interface VoucherListParams {
  status?: string;
  /** Nguồn tiền trên màn "Tất cả thu chi" — KHÔNG kéo theo chuyển nội bộ. */
  payment_method?: 'cash' | 'bank' | 'card' | 'other';
  /** Màn Tiền mặt / Tiền gửi — CÓ kéo theo chuyển nội bộ (phiếu NB thuộc cả hai màn). */
  fund_type?: 'cash' | 'bank';
  object_type?: 'customer' | 'supplier' | 'employee' | 'other';
  store_id?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
}

export interface VoucherTypeSummary {
  total: number;
  PT: number;
  PC: number;
  NB: number;
}

export const accountingService = {
  /**
   * Get dashboard summary data
   * GET /accounting/dashboard/summary
   */
  getDashboardSummary(params: DashboardSummaryParams): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/dashboard', { params })
      .then((r) => r.data);
  },

  /**
   * Get recent transactions
   * GET /accounting/dashboard/transactions
   */
  getRecentTransactions(params: RecentTransactionsParams): Promise<ApiResponse<any[]>> {
    return api
      .get('/accounting/dashboard/transactions', { params })
      .then((r) => r.data);
  },

  // ==================== Financial Reports ====================

  /**
   * Get business results report (Kết quả kinh doanh)
   * GET /accounting/reports/income-statement
   */
  getBusinessResultsReport(params: { year: number; month: number; from_date?: string; to_date?: string; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/reports/income-statement', { params })
      .then((r) => r.data);
  },

  getBusinessResultsAmisReport(params: { from_date: string; to_date: string; store_id?: string }): Promise<ApiResponse<{ periods: Record<string, Record<string, number>> }>> {
    return api
      .get('/accounting/reports/income-statement-amis', { params })
      .then((r) => r.data);
  },

  /**
   * Get balance sheet report (Bảng cân đối kế toán)
   * GET /accounting/reports/balance-sheet
   */
  getBalanceSheetReport(params: { year: number; month: number; as_of_date?: string; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/reports/balance-sheet', { params })
      .then((r) => r.data);
  },

  /**
   * Get trial balance report (Bảng cân đối phát sinh)
   * GET /accounting/reports/trial-balance
   */
  getTrialBalanceReport(params: { year: number; month: number; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/reports/trial-balance', { params })
      .then((r) => r.data);
  },

  /**
   * Get S2b revenue ledger (Sổ S2b - Doanh thu bán hàng và dịch vụ)
   * GET /accounting/reports/s2b-revenue
   */
  getS2bRevenueLedger(params: { year: number; month: number; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/books/hkd/S2b', { params })
      .then((r) => r.data);
  },

  /**
   * Get S2c income expense detail (Sổ S2c - Doanh thu/Chi phí chi tiết)
   * GET /accounting/reports/s2c-income-expense
   */
  getS2cIncomeExpenseDetail(params: { year: number; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/books/hkd/S2c', { params })
      .then((r) => r.data);
  },

  /**
   * Get S2d materials detail (Sổ S2d - Chi tiết vật tư)
   * GET /accounting/reports/s2d-materials
   */
  getS2dMaterialsDetail(params: { variant_code?: string; year: number; month: number; warehouse_id?: string; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/books/hkd/S2d', { params })
      .then((r) => r.data);
  },

  /**
   * Get S2e cash detail (Sổ S2e - Chi tiết tiền)
   * GET /accounting/reports/s2e-cash
   */
  getS2eCashDetail(params: { tab?: string; from?: string; to?: string; account_code?: string; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/books/hkd/S2e', { params })
      .then((r) => r.data);
  },

  /**
   * Get inventory summary report (Tổng hợp nhập xuất tồn)
   * GET /accounting/reports/inventory-summary
   */
  getInventorySummaryReport(params: { warehouse_id?: string; year: number; month: number; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/reports/inventory-summary', { params })
      .then((r) => r.data);
  },

  /**
   * Get cash book (Sổ quỹ tiền mặt / Tiền gửi)
   * GET /accounting/cash-bank-books/cash
   */
  getCashBook(params: { tab?: string; fund_code?: string; year?: number; month?: number; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/cash-bank-books/cash', { params })
      .then((r) => r.data);
  },

  /**
   * Get bank deposit book (Sổ tiền gửi ngân hàng)
   * GET /accounting/cash-bank-books/bank
   */
  getBankBook(params: { tab?: string; fund_code?: string; year?: number; month?: number; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/cash-bank-books/bank', { params })
      .then((r) => r.data);
  },

  /**
   * Sổ Nhật ký thu/chi tiền
   * GET /accounting/cash-bank-books/receipt|payment/journal
   */
  getCashJournal(type: 'receipt' | 'payment', params: { year: number; month: number; fund_code?: string; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get(`/accounting/cash-bank-books/${type}/journal`, { params })
      .then((r) => r.data);
  },

  auditAccountingData(params?: AccountingRepairAuditParams): Promise<ApiResponse<AccountingRepairAuditResult>> {
    return api
      .get('/accounting/data-repair/audit', params ? { params } : undefined)
      .then((r) => r.data);
  },

  repairAccountingBalances(dryRun = true): Promise<ApiResponse<AccountingBalanceRepairResult>> {
    return api
      .post('/accounting/data-repair/repair-balances', { dry_run: dryRun })
      .then((r) => r.data);
  },

  repairAccountingDataSafe(dryRun = true): Promise<ApiResponse<AccountingSafeRepairResult>> {
    return api
      .post('/accounting/data-repair/repair-safe', { dry_run: dryRun })
      .then((r) => r.data);
  },

  /**
   * Get document listing (Bảng kê chứng từ)
   * GET /accounting/reports/document-listing
   */
  getDocumentListing(params: { doc_type?: string; from: string; to: string; store_id?: string; page?: number; page_size?: number }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/reports/document-listing', { params })
      .then((r) => r.data);
  },

  /**
   * Get voucher listing (Bảng kê phiếu thu/chi)
   * GET /accounting/reports/voucher-listing
   */
  getVoucherListing(params: { voucher_type?: string; fund_code?: string; fund_account_id?: string; from: string; to: string; store_id?: string; page?: number; page_size?: number }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/reports/voucher-listing', { params })
      .then((r) => r.data);
  },

  /**
   * Get invoice listing (Bảng kê hóa đơn)
   * GET /accounting/reports/invoice-listing
   */
  getInvoiceListing(params: { invoice_type?: string; payment_method?: string; from: string; to: string; page?: number; page_size?: number; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/reports/invoice-listing', { params })
      .then((r) => r.data);
  },

  /**
   * Get warehouse voucher listing (Bảng kê phiếu nhập/xuất kho)
   * GET /accounting/reports/warehouse-voucher-listing
   */
  getWarehouseVoucherListing(params: { voucher_type?: string; warehouse_id?: string; from?: string; to?: string; store_id?: string; page?: number; page_size?: number }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/reports/warehouse-voucher-listing', { params })
      .then((r) => r.data);
  },

  /**
   * Get receivable/payable listing (Bảng kê công nợ phải thu/phải trả)
   * GET /accounting/reports/receivable-payable
   */
  getReceivablePayableListing(params: { type?: string; as_of_date?: string; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/reports/receivable-payable', { params })
      .then((r) => r.data);
  },

  getSupplierPayableBalances(params?: { as_of_date?: string; store_id?: string }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/reports/ap-balances', { params })
      .then((r) => r.data);
  },

  // ==================== Prepaid Expenses ====================

  // ==================== Vouchers (Phiếu thu/chi) ====================

  getVouchers(params: VoucherListParams & {
    type?: 'PT' | 'PC' | 'NB';
    fund_account_id?: string;
    include_internal?: boolean;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<any>> {
    return api.get('/accounting/vouchers', { params }).then((r) => r.data);
  },

  /**
   * TCR-1436 — số phiếu theo loại cho badge trên tab. KHÔNG nhận `type`: badge của cả 4 tab
   * phải đếm trên cùng một tập, không co lại theo tab đang mở.
   */
  getVoucherSummary(params: VoucherListParams): Promise<ApiResponse<VoucherTypeSummary>> {
    return api.get('/accounting/vouchers/summary', { params }).then((r) => r.data);
  },

  getVoucherDetail(id: string): Promise<ApiResponse<any>> {
    return api.get(`/accounting/vouchers/${id}`).then((r) => r.data);
  },

  /**
   * POST /accounting/vouchers/:id/accounting-sync/retry
   * "Thử lại" đồng bộ AMIS cho phiếu thu đang lỗi — giữ nguyên snapshot cũ, dùng cho lỗi
   * hạ tầng (mạng, time-out AMIS).
   */
  retryVoucherSync(id: string): Promise<ApiResponse<SyncBadge>> {
    return api.post(`/accounting/vouchers/${id}/accounting-sync/retry`).then((r) => r.data);
  },

  /**
   * POST /accounting/vouchers/:id/accounting-sync/resync
   * "Cập nhật & đồng bộ lại" — dựng lại snapshot từ phiếu hiện tại, dùng sau khi kế toán đã
   * sửa dữ liệu nghiệp vụ gây lỗi.
   */
  resyncVoucher(id: string): Promise<ApiResponse<SyncBadge>> {
    return api.post(`/accounting/vouchers/${id}/accounting-sync/resync`).then((r) => r.data);
  },

  createVoucher(data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post('/accounting/vouchers', data).then((r) => r.data);
  },

  updateVoucher(id: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.patch(`/accounting/vouchers/${id}`, data).then((r) => r.data);
  },

  updatePostedVoucher(id: string, data: PostedAccountingVoucherUpdate): Promise<ApiResponse<unknown>> {
    return api.patch(`/accounting/vouchers/${id}`, data).then((r) => r.data);
  },

  deleteVoucher(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/vouchers/${id}`).then((r) => r.data);
  },

  postVoucher(id: string): Promise<ApiResponse<any>> {
    return api.post(`/accounting/vouchers/${id}/post`).then((r) => r.data);
  },

  getAttachments(ownerId: string): Promise<ApiResponse<AccountingAttachment[]>> {
    return api.get('/accounting/documents/attachments', {
      params: { owner_type: 'cash_voucher', owner_id: ownerId },
    }).then((r) => r.data);
  },

  createAttachment(data: CreateAccountingAttachmentInput): Promise<ApiResponse<AccountingAttachment>> {
    return api.post('/accounting/documents/attachments/media', data).then((r) => r.data);
  },

  getAttachmentDownload(id: string): Promise<ApiResponse<{ downloadUrl: string; fileName: string }>> {
    return api.get(`/accounting/documents/attachments/${id}/download`).then((r) => r.data);
  },

  deleteAttachment(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/documents/attachments/${id}`).then((r) => r.data);
  },

  /**
   * POST /accounting/vouchers/:id/cancel
   * Cancel posted voucher + auto-reverse JE (EG-443)
   */
  cancelVoucher(id: string, reason: string): Promise<ApiResponse<any>> {
    return api.post(`/accounting/vouchers/${id}/cancel`, { reason }).then((r) => r.data);
  },

  /**
   * GET /accounting/vouchers/:id/unpost-impact
   * EG-583: Preview unpost impact
   */
  getVoucherUnpostImpact(id: string): Promise<ApiResponse<any>> {
    return api.get(`/accounting/vouchers/${id}/unpost-impact`).then((r) => r.data);
  },

  /**
   * POST /accounting/vouchers/:id/unpost
   * EG-583: Unpost voucher (posted → draft) + reverse JE
   */
  unpostVoucher(id: string, reason: string): Promise<ApiResponse<any>> {
    return api.post(`/accounting/vouchers/${id}/unpost`, { reason }).then((r) => r.data);
  },

  /**
   * GET /accounting/vouchers/:id/history
   * TCR-742: nhật ký thao tác của phiếu (tạo / sửa / ghi sổ / bỏ ghi sổ + lý do).
   */
  getVoucherHistory(id: string): Promise<{ data: VoucherHistoryEntry[] }> {
    return api.get(`/accounting/vouchers/${id}/history`).then((r) => r.data);
  },

  /**
   * POST /accounting/vouchers/:id/finalize
   * Finalize draft voucher → posted with official number (K02)
   */
  finalizeVoucher(id: string): Promise<ApiResponse<any>> {
    return api.post(`/accounting/vouchers/${id}/finalize`).then((r) => r.data);
  },

  /**
   * GET /accounting/vouchers/:id/print
   * Print voucher PDF/HTML
   */
  printVoucher(id: string, format: 'pdf' | 'html' = 'pdf'): Promise<any> {
    return api.get(`/accounting/vouchers/${id}/print`, { params: { format } }).then((r) => r.data);
  },

  /**
   * POST /accounting/vouchers/special
   * Create special accounting voucher (PKT) - EG-439
   */
  createSpecialVoucher(data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post('/accounting/vouchers/special', data).then((r) => r.data);
  },

  /**
   * GET /accounting/ar-balances
   * Customer AR balance summary
   */
  getArBalances(params?: { customer_id?: string; as_of_date?: string; store_id?: string }): Promise<ApiResponse<any>> {
    return api.get('/accounting/ar-balances', { params }).then((r) => r.data);
  },

  /**
   * POST /accounting/ar-ap-offset
   * AR/AP offset (bù trừ công nợ)
   */
  createArApOffset(data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post('/accounting/ar-ap-offset', data).then((r) => r.data);
  },

  /**
   * GET /accounting/reports/cash-flow
   * Cash flow statement (Báo cáo lưu chuyển tiền tệ)
   */
  getCashFlowReport(params: { year: number; month: number; store_id?: string }): Promise<ApiResponse<any>> {
    return api.get('/accounting/reports/cash-flow', { params }).then((r) => r.data);
  },

  /**
   * POST /accounting/prepaid-expenses/run-allocation
   * Run manual allocation for specific prepaid expense
   */
  runPrepaidAllocation(id: string, data: { year: number; month: number }): Promise<ApiResponse<any>> {
    return api.post(`/accounting/prepaid-expenses/${id}/post-allocation`, data).then((r) => r.data);
  },

  /**
   * POST /accounting/prepaid-expenses/:id/adjust
   * Adjust allocation amount
   */
  adjustPrepaidAllocation(id: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post(`/accounting/prepaid-expenses/${id}/adjust`, data).then((r) => r.data);
  },

  /**
   * GET /accounting/prepaid-expenses/report
   * Prepaid expense allocation report
   */
  getPrepaidExpenseReport(params?: { year?: number; status?: string }): Promise<ApiResponse<any>> {
    return api.get('/accounting/prepaid-expenses/report', { params }).then((r) => r.data);
  },

  getFundAccounts(params?: {
    type?: 'cash' | 'bank' | 'other' | 'all';
    store_id?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<any>> {
    return api.get('/accounting/fund-accounts', { params }).then((r) => r.data);
  },


  getBankStatementLines(params?: {
    fund_account_id?: string;
    status?: string;
    from_date?: string;
    to_date?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/bank-statements/lines', { params }).then((r) => r.data);
  },

  importBankStatement(data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post('/accounting/bank-statements/import', data).then((r) => r.data);
  },

  suggestBankStatementMatches(lineId: string): Promise<ApiResponse<any[]>> {
    return api.post(`/accounting/bank-statements/lines/${lineId}/suggest`).then((r) => r.data);
  },

  matchBankStatementLine(lineId: string, data: { voucher_id: string; confidence?: number; reason?: string }): Promise<ApiResponse<any>> {
    return api.post(`/accounting/bank-statements/lines/${lineId}/match`, data).then((r) => r.data);
  },

  unmatchBankStatementLine(lineId: string): Promise<ApiResponse<any>> {
    return api.post(`/accounting/bank-statements/lines/${lineId}/unmatch`).then((r) => r.data);
  },

  createVoucherFromBankStatementLine(lineId: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post(`/accounting/bank-statements/lines/${lineId}/create-voucher`, data).then((r) => r.data);
  },

  getBankReconciliations(params?: { fund_account_id?: string }): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/bank-statements/reconciliations', { params }).then((r) => r.data);
  },

  previewBankReconciliation(data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post('/accounting/bank-statements/reconciliations/preview', data).then((r) => r.data);
  },

  createBankReconciliation(data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post('/accounting/bank-statements/reconciliations', data).then((r) => r.data);
  },

  confirmBankReconciliation(id: string): Promise<ApiResponse<any>> {
    return api.post(`/accounting/bank-statements/reconciliations/${id}/confirm`).then((r) => r.data);
  },

  reopenBankReconciliation(id: string): Promise<ApiResponse<any>> {
    return api.post(`/accounting/bank-statements/reconciliations/${id}/reopen`).then((r) => r.data);
  },

  getBankMatchingRules(): Promise<ApiResponse<any[]>> {
    return api.get('/accounting/bank-statements/rules').then((r) => r.data);
  },

  createBankMatchingRule(data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post('/accounting/bank-statements/rules', data).then((r) => r.data);
  },

  updateBankMatchingRule(id: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/bank-statements/rules/${id}`, data).then((r) => r.data);
  },

  deleteBankMatchingRule(id: string): Promise<ApiResponse<any>> {
    return api.delete(`/accounting/bank-statements/rules/${id}`).then((r) => r.data);
  },

  getCategories(params?: { type?: 'income' | 'expense' }): Promise<ApiResponse<any>> {
    return api.get('/accounting/categories', { params }).then((r) => r.data);
  },

  // ==================== Prepaid Expenses ====================

  /**
   * GET /accounting/prepaid-expenses
   * Danh sách chi phí trả trước
   */
  getPrepaidExpenses(params: {
    status?: string;
    store_id?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/prepaid-expenses', { params })
      .then((r) => r.data);
  },

  /**
   * GET /accounting/prepaid-expenses/:id
   * Chi tiết chi phí trả trước
   */
  getPrepaidExpenseDetail(id: string): Promise<ApiResponse<any>> {
    return api.get(`/accounting/prepaid-expenses/${id}`).then((r) => r.data);
  },

  /**
   * GET /accounting/prepaid-expenses/available-source-documents
   * Lấy danh sách chứng từ nguồn có Nợ TK 242 chưa liên kết
   */
  getAvailablePrepaidSourceDocs(params: { from_date: string; to_date: string; prepaid_account_code?: string }): Promise<ApiResponse<any>> {
    return api.get('/accounting/prepaid-expenses/available-source-documents', { params }).then((r) => r.data);
  },

  /**
   * POST /accounting/prepaid-expenses
   * Tạo chi phí trả trước mới
   */
  createPrepaidExpense(data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post('/accounting/prepaid-expenses', data).then((r) => r.data);
  },

  /**
   * PUT /accounting/prepaid-expenses/:id
   * Cập nhật chi phí trả trước
   */
  updatePrepaidExpense(
    id: string,
    data: Record<string, unknown>
  ): Promise<ApiResponse<any>> {
    return api.put(`/accounting/prepaid-expenses/${id}`, data).then((r) => r.data);
  },

  /**
   * DELETE /accounting/prepaid-expenses/:id
   * Xóa chi phí trả trước
   */
  deletePrepaidExpense(id: string, force = false): Promise<ApiResponse<any>> {
    return api.delete(`/accounting/prepaid-expenses/${id}`, { params: force ? { force: 'true' } : {} }).then((r) => r.data);
  },

  /**
   * GET /accounting/prepaid-expenses/:id/summary
   * Tổng quan phân bổ chi phí trả trước
   */
  getPrepaidExpenseSummary(id: string): Promise<ApiResponse<any>> {
    return api.get(`/accounting/prepaid-expenses/${id}/summary`).then((r) => r.data);
  },

  /**
   * GET /accounting/prepaid-expenses/:id/accounting-trail
   * Chứng từ gốc và bút toán liên quan
   */
  getPrepaidExpenseAccountingTrail(id: string): Promise<ApiResponse<unknown>> {
    return api.get(`/accounting/prepaid-expenses/${id}/accounting-trail`).then((r) => r.data);
  },

  /**
   * GET /accounting/prepaid-expenses/monthly-allocations
   * Danh sách phân bổ chờ xử lý cho một tháng
   */
  getMonthlyAllocations(
    year: number,
    month: number,
    method?: string
  ): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/prepaid-expenses/monthly-allocations', {
        params: { year, month, method },
      })
      .then((r) => r.data);
  },

  /**
   * POST /accounting/prepaid-expenses/monthly-allocations/post
   * Post phân bổ hàng loạt cho một tháng
   */
  postMonthlyAllocations(
    year: number,
    month: number,
    method?: string
  ): Promise<ApiResponse<any>> {
    return api
      .post('/accounting/prepaid-expenses/monthly-allocations/post', {
        year,
        month,
        method,
      })
      .then((r) => r.data);
  },

  // ==================== Fixed Asset Groups (TSCĐ — Nhóm) ====================

  /**
   * GET /accounting/fixed-asset-groups
   */
  getFixedAssetGroups(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ApiResponse<any>> {
    return api.get('/accounting/fixed-asset-groups', { params }).then((r) => r.data);
  },

  /**
   * GET /accounting/fixed-asset-groups/:id
   */
  getFixedAssetGroupDetail(id: string): Promise<ApiResponse<any>> {
    return api.get(`/accounting/fixed-asset-groups/${id}`).then((r) => r.data);
  },

  /**
   * POST /accounting/fixed-asset-groups
   */
  createFixedAssetGroup(data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post('/accounting/fixed-asset-groups', data).then((r) => r.data);
  },

  /**
   * PATCH /accounting/fixed-asset-groups/:id
   */
  updateFixedAssetGroup(id: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.patch(`/accounting/fixed-asset-groups/${id}`, data).then((r) => r.data);
  },

  // ==================== Fixed Assets (TSCĐ) ====================

  /**
   * GET /accounting/fixed-assets
   */
  getFixedAssets(params?: {
    page?: number;
    limit?: number;
    status?: string;
    asset_group_id?: string;
    department_id?: string;
    search?: string;
    store_id?: string;
  }): Promise<ApiResponse<any>> {
    return api.get('/accounting/fixed-assets', { params }).then((r) => r.data);
  },

  /**
   * GET /accounting/fixed-assets/:id
   */
  getFixedAssetDetail(id: string): Promise<ApiResponse<any>> {
    return api.get(`/accounting/fixed-assets/${id}`).then((r) => r.data);
  },

  /**
   * POST /accounting/fixed-assets
   */
  createFixedAsset(data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post('/accounting/fixed-assets', data).then((r) => r.data);
  },

  /**
   * PUT /accounting/fixed-assets/:id
   */
  updateFixedAsset(id: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/fixed-assets/${id}`, data).then((r) => r.data);
  },

  /**
   * DELETE /accounting/fixed-assets/:id
   */
  deleteFixedAsset(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/fixed-assets/${id}`).then((r) => r.data);
  },

  /**
   * GET /accounting/fixed-assets/:id/depreciation-schedule
   */
  getFixedAssetDepreciationSchedule(id: string): Promise<ApiResponse<any>> {
    return api.get(`/accounting/fixed-assets/${id}/depreciation-schedule`).then((r) => r.data);
  },

  /**
   * POST /accounting/fixed-assets/:id/liquidate
   */
  liquidateFixedAsset(id: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post(`/accounting/fixed-assets/${id}/liquidate`, data).then((r) => r.data);
  },

  /**
   * POST /accounting/fixed-assets/:id/transfer
   */
  transferFixedAsset(id: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post(`/accounting/fixed-assets/${id}/transfer`, data).then((r) => r.data);
  },

  /**
   * POST /accounting/fixed-assets/:id/upgrade
   */
  upgradeFixedAsset(id: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post(`/accounting/fixed-assets/${id}/upgrade`, data).then((r) => r.data);
  },

  // ==================== Fixed Asset Depreciation ====================

  /**
   * GET /accounting/fixed-assets/depreciation/preview
   */
  previewDepreciation(year: number, month: number): Promise<ApiResponse<any>> {
    return api.get('/accounting/fixed-assets/depreciation/preview', { params: { year, month } }).then((r) => r.data);
  },

  /**
   * POST /accounting/fixed-assets/depreciation/post
   */
  postDepreciation(year: number, month: number): Promise<ApiResponse<any>> {
    return api.post('/accounting/fixed-assets/depreciation/post', { year, month }).then((r) => r.data);
  },

  postMonthlyDepreciation(year: number, month: number): Promise<ApiResponse<any>> {
    return api.post('/accounting/fixed-assets/depreciation/post', { year, month }).then((r) => r.data);
  },


  /**
   * S10-DN - Sổ chi tiết vật liệu, dụng cụ (Doanh nghiệp TT99/TT200)
   * GET /inventory/reports/S10-DN
   */
  getS10dnMaterialsDetail(params: { variant_code?: string; year: number; month: number; warehouse_id?: string; store_id?: string }): Promise<ApiResponse<any>> {
    return api.get('/inventory/reports/S10-DN', { params }).then((r) => r.data);
  },

  // ==================== Tool & Supplies (CCDC) ====================

  /**
   * GET /accounting/tool-supplies
   */
  getToolSupplies(params?: {
    page?: number;
    limit?: number;
    status?: string;
    department_id?: string;
    warehouse_id?: string;
    search?: string;
    store_id?: string;
  }): Promise<ApiResponse<any>> {
    return api.get('/accounting/tool-supplies', { params }).then((r) => r.data);
  },

  /**
   * GET /accounting/tool-supplies/:id
   */
  getToolSupplyDetail(id: string): Promise<ApiResponse<any>> {
    return api.get(`/accounting/tool-supplies/${id}`).then((r) => r.data);
  },

  /**
   * POST /accounting/tool-supplies
   */
  createToolSupply(data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post('/accounting/tool-supplies', data).then((r) => r.data);
  },

  /**
   * PUT /accounting/tool-supplies/:id
   */
  updateToolSupply(id: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.put(`/accounting/tool-supplies/${id}`, data).then((r) => r.data);
  },

  /**
   * DELETE /accounting/tool-supplies/:id
   */
  deleteToolSupply(id: string, force?: boolean): Promise<ApiResponse<void>> {
    const params = force ? { force: 'true' } : undefined;
    return api.delete(`/accounting/tool-supplies/${id}`, { params }).then((r) => r.data);
  },

  /**
   * POST /accounting/tool-supplies/:id/reverse-allocation
   */
  reverseToolSupplyAllocation(id: string, data: { year: number; month: number }): Promise<ApiResponse<any>> {
    return api.post(`/accounting/tool-supplies/${id}/reverse-allocation`, data).then((r) => r.data);
  },

  /**
   * GET /accounting/tool-supplies/:id/summary
   */
  getToolSupplySummary(id: string): Promise<ApiResponse<any>> {
    return api.get(`/accounting/tool-supplies/${id}/summary`).then((r) => r.data);
  },

  /**
   * GET /accounting/tool-supplies/:id/accounting-trail
   */
  getToolSupplyAccountingTrail(id: string): Promise<ApiResponse<any>> {
    return api.get(`/accounting/tool-supplies/${id}/accounting-trail`).then((r) => r.data);
  },

  /**
   * POST /accounting/tool-supplies/:id/post-allocation
   */
  postToolSupplyAllocation(id: string, data: { year: number; month: number }): Promise<ApiResponse<any>> {
    return api.post(`/accounting/tool-supplies/${id}/post-allocation`, data).then((r) => r.data);
  },

  /**
   * GET /accounting/tool-supplies/monthly-allocations/preview
   */
  getToolSupplyMonthlyAllocations(year: number, month: number): Promise<ApiResponse<any>> {
    return api
      .get('/accounting/tool-supplies/monthly-allocations/preview', { params: { year, month } })
      .then((r) => r.data);
  },

  /**
   * POST /accounting/tool-supplies/monthly-allocations/post
   */
  postToolSupplyMonthlyAllocations(year: number, month: number): Promise<ApiResponse<any>> {
    return api
      .post('/accounting/tool-supplies/monthly-allocations/post', { year, month })
      .then((r) => r.data);
  },

  /**
   * POST /accounting/tool-supplies/:id/recover
   */
  recoverToolSupply(id: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post(`/accounting/tool-supplies/${id}/recover`, data).then((r) => r.data);
  },

  /**
   * POST /accounting/tool-supplies/:id/dispose
   */
  disposeToolSupply(id: string, data: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.post(`/accounting/tool-supplies/${id}/dispose`, data).then((r) => r.data);
  },

  // ================= Phase 2: Source Documents =================

  /**
   * GET /accounting/tool-supplies/source-documents
   */
  getSourceDocuments(params?: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.get('/accounting/tool-supplies/source-documents', { params }).then((r) => r.data);
  },

  /**
   * GET /accounting/tool-supplies/source-documents/:sourceType/:id/lines
   */
  getSourceDocumentLines(sourceType: string, id: string): Promise<ApiResponse<any>> {
    return api.get(`/accounting/tool-supplies/source-documents/${sourceType}/${id}/lines`).then((r) => r.data);
  },

  /**
   * POST /accounting/tool-supplies/import-excel
   */
  importToolSupplies(formData: FormData): Promise<ApiResponse<any>> {
    return api.post('/accounting/tool-supplies/import-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },

  // ================= Phase 8: Reports =================

  /**
   * GET /accounting/tool-supplies/reports/:reportKey
   */
  getToolSupplyReport(reportKey: string, params?: Record<string, unknown>): Promise<ApiResponse<any>> {
    return api.get(`/accounting/tool-supplies/reports/${reportKey}`, { params }).then((r) => r.data);
  },

  // ================= Payment Terms (Accounting Options) =================

  getPaymentTerms(type?: string): Promise<ApiResponse<any>> {
    return api.get('/accounting/options', { params: { type: type || 'payment_term' } }).then((r) => r.data);
  },

  createPaymentTerm(data: { type: string; name: string; value?: number }): Promise<ApiResponse<any>> {
    return api.post('/accounting/options', data).then((r) => r.data);
  },

  // ================= Purchasing Reports (TCR-266, TCR-267, TCR-268) =================

  /**
   * GET /accounting/reports/ap-summary — Sổ 331 tổng hợp
   */
  getApSummary(params: { from?: string; to?: string; account_code?: string; store_id?: string }): Promise<ApiResponse<any>> {
    return api.get('/accounting/reports/ap-summary', { params }).then((r) => r.data);
  },

  /**
   * GET /accounting/reports/ap-detail — Sổ chi tiết 331
   */
  getApDetail(params: { supplier_id?: string; from?: string; to?: string; store_id?: string }): Promise<ApiResponse<any>> {
    return api.get('/accounting/reports/ap-detail', { params }).then((r) => r.data);
  },

  /**
   * GET /accounting/reports/ar-detail — Sổ chi tiết 131
   */
  getArDetail(params: { customer_id?: string; customer_group_id?: string; from?: string; to?: string; store_id?: string }): Promise<ApiResponse<any>> {
    return api.get('/accounting/reports/ar-detail', { params }).then((r) => r.data);
  },

  /**
   * GET /accounting/reports/ar-summary — Sổ 131 tổng hợp
   */
  getArSummary(params: { from?: string; to?: string; account_code?: string; store_id?: string | string[]; customer_group_id?: string; customer_id?: string }): Promise<ApiResponse<any>> {
    return api.get('/accounting/reports/ar-summary', { params }).then((r) => r.data);
  },

  /**
   * GET /accounting/reports/purchase-ledger — Sổ chi tiết mua hàng
   */
  getSalesDetail(params: { from?: string; to?: string; store_id?: string; product_id?: string; customer_id?: string; customer_group_id?: string; staff_id?: string }): Promise<ApiResponse<any>> {
    return api.get('/accounting/reports/sales-detail', { params }).then((r) => r.data);
  },

  getSalesSummaryByProduct(params: {
    from?: string;
    to?: string;
    store_id?: string;
    product_group_id?: string;
    product_id?: string;
    customer_group_id?: string;
    page?: number;
    page_size?: number;
  }): Promise<ApiResponse<{ items: SalesSummaryByProductItem[]; totals: SalesSummaryByProductTotals }>> {
    return api.get('/accounting/reports/sales-summary-by-product', { params }).then((r) => r.data);
  },

  getPurchaseLedger(params: { supplier_id?: string; from?: string; to?: string; store_id?: string; product_id?: string; variant_id?: string; item_code?: string }): Promise<ApiResponse<any>> {
    return api.get('/accounting/reports/purchase-ledger', { params }).then((r) => r.data);
  },
};

export default accountingService;
